import { useRef, type KeyboardEvent, type ReactElement } from 'react';

import { Icon } from '@/ui/components/Icon';

import styles from './TabBar.module.css';
import { panelDomId, tabDomId, type ViewTab } from './types';

/**
 * The view strip — ADR-0020, docs/ARCHITECTURE.md §8.2, recon §1.1.
 *
 * ═══ IT RENDERS NOTHING WHILE THERE IS ONE VIEW ═══
 *
 * v1 ships exactly one view, so v1 shows no tab strip at all: a single tab is a control that
 * cannot change anything, occupying a row of chrome to advertise a choice that does not
 * exist. The legacy build did the opposite in two places at once — `TabsContainer.tsx`
 * hardcoded eight tabs and sliced them at a literal `4`, and the language `<select>` was
 * drawn for exactly one language. Both are the same mistake: UI for an option with one value.
 *
 * `<= 1` and not `=== 1`: a registry emptied by a bad edit must also render nothing, rather
 * than an empty `tablist` that a screen reader announces and a keyboard user can enter.
 *
 * ═══ WHY THIS IS A REAL tablist ═══
 *
 * A `role="tab"` promises arrow-key navigation and one tab stop, so both are implemented
 * here — `ArrowLeft`/`ArrowRight`/`Home`/`End`, and `tabIndex={-1}` on everything that is not
 * selected. This does NOT contradict DESIGN §7.4's "Tab traverses in visual order": that rule
 * is about the toolbar, where each action is an independent destination. A tab strip is one
 * control with several settings, and a screen-reader user who hears "tab, 2 of 4" and then
 * finds the arrow keys dead has been told something untrue about the widget.
 *
 * Selection follows focus. Every panel is `select(doc)` — a pure, synchronous, memoised
 * projection — plus a lazily mounted component, so arrowing through the strip cannot be
 * expensive enough to need the "activate manually" variant of the pattern.
 */

export interface TabBarProps {
  readonly views: readonly ViewTab[];
  readonly activeId: string;
  readonly onSelect: (id: string) => void;
  /** Localised. Names the strip — e.g. "Views". */
  readonly label: string;
  /** Localised word for a view marked `beta`. Omitted ⇒ the marker is not drawn. */
  readonly betaLabel?: string | undefined;
}

export function TabBar({
  views,
  activeId,
  onSelect,
  label,
  betaLabel,
}: TabBarProps): ReactElement | null {
  const strip = useRef<HTMLDivElement | null>(null);

  if (views.length <= 1) return null;

  const selectable = views.filter((view) => view.disabled !== true);

  const move = (from: number, delta: number): void => {
    if (selectable.length === 0) return;
    const length = selectable.length;
    const next = selectable[(((from + delta) % length) + length) % length];
    if (next === undefined) return;
    onSelect(next.id);
    // Focus follows selection, so the roving `tabIndex` and the DOM focus stay in step. The
    // element is found by id rather than by a ref map: there is one strip, the ids are
    // derived in `types.ts`, and a `Map<string, HTMLElement>` here would be a second thing to
    // keep in sync with the registry.
    strip.current?.ownerDocument.getElementById(tabDomId(next.id))?.focus();
  };

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    const current = selectable.findIndex((view) => view.id === activeId);
    if (current < 0) return;

    switch (event.key) {
      case 'ArrowRight':
        move(current, 1);
        break;
      case 'ArrowLeft':
        move(current, -1);
        break;
      case 'Home':
        move(0, 0);
        break;
      case 'End':
        move(selectable.length - 1, 0);
        break;
      default:
        return;
    }
    // Only after a key that was actually handled: swallowing Home/End unconditionally would
    // break the document-start shortcut for anyone who reached the strip by accident.
    event.preventDefault();
  };

  return (
    <div
      ref={strip}
      role="tablist"
      aria-label={label}
      aria-orientation="horizontal"
      className={styles.bar}
      onKeyDown={onKeyDown}
    >
      {views.map((view) => {
        const selected = view.id === activeId;
        return (
          <button
            key={view.id}
            id={tabDomId(view.id)}
            type="button"
            role="tab"
            aria-selected={selected}
            aria-controls={panelDomId(view.id)}
            // The name is stated rather than taken from the visible text, because at ≤900px
            // there IS no visible text — the label collapses to its icon like the toolbar's
            // ghost group does, and an unnamed tab is not a thing this component may render
            // (docs/DESIGN.md §7.5).
            aria-label={view.label}
            title={view.label}
            // One tab stop for the whole strip: Tab enters it at the selected tab, and the
            // arrow keys move within it.
            tabIndex={selected ? 0 : -1}
            disabled={view.disabled === true}
            className={selected ? `${styles.tab} ${styles.selected}` : styles.tab}
            onClick={() => {
              onSelect(view.id);
            }}
          >
            <Icon name={view.icon} size={15} />
            <span className={styles.label}>{view.label}</span>
            {view.beta === true && betaLabel !== undefined ? (
              <span className={styles.beta}>{betaLabel}</span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}

export default TabBar;
